import React from 'react';
import { Box, Typography } from '@mui/material';
import { LuCheck, LuArrowRight } from 'react-icons/lu';
import { elliottTokens as t } from './tokens';

type Icon = 'check' | 'arrow' | 'none';

type OptionPillProps = {
  label: string;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  // 'cta' is always brandy-filled (Continue / Design Now); 'choice' fills only when selected.
  variant?: 'choice' | 'cta';
  icon?: Icon;
};

// Compact inline pill used for single picks (owner mode, design choice) and
// multi-select chips (audience). Brandy-filled when selected or when it's a CTA.
export const OptionPill: React.FC<OptionPillProps> = ({
  label,
  onClick,
  selected = false,
  disabled = false,
  variant = 'choice',
  icon
}) => {
  // A disabled CTA takes the house disabled look (dark, dim text), not a faded orange.
  const ctaDisabled = variant === 'cta' && disabled;
  const filled = (variant === 'cta' && !disabled) || selected;
  // CTAs carry no arrow; choice pills show a check once selected.
  const resolvedIcon: Icon = icon ?? (selected && variant !== 'cta' ? 'check' : 'none');

  return (
    <Box
      data-testid="option-pill"
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        px: '12px',
        py: '7px',
        borderRadius: '6px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !ctaDisabled ? 0.5 : 1,
        // Unselected options carry a brandy outline — the "act here" highlight
        // moves to the options (and off the reply bar) when a choice is needed.
        border: filled
          ? '1.5px solid #f8de89'
          : disabled
          ? `1px solid ${t.borderContrast}`
          : '1.5px solid rgba(247,148,32,0.55)',
        // CTAs (Proceed / Continue / …) use a solid brandy fill + white text;
        // selected option pills keep the softer brandy fill.
        background: ctaDisabled
          ? 'rgba(0, 0, 0, 0.2)'
          : variant === 'cta'
          ? 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
          : selected
          ? t.brandyFill
          : t.surface,
        transition: 'filter 0.15s ease, border-color 0.15s ease',
        '&:hover': disabled
          ? undefined
          : filled
          ? { filter: 'brightness(1.08)' }
          : { borderColor: t.brandLight }
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: filled ? 600 : 500,
          lineHeight: 1.4,
          color: ctaDisabled ? '#565656' : filled ? t.textPrimary : t.textSecondary,
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
      {resolvedIcon !== 'none' && (
        <Box sx={{ display: 'flex', color: filled ? t.textPrimary : t.textTertiary }}>
          {resolvedIcon === 'arrow' ? (
            <LuArrowRight size={15} strokeWidth={2.5} />
          ) : (
            <LuCheck size={15} strokeWidth={2.5} />
          )}
        </Box>
      )}
    </Box>
  );
};

// Full-width selectable card with an optional colour dot + description.
// Used for the Category / Program Type / Build Method steps. Supports a
// disabled state with an optional badge (e.g. Template "Coming Soon").
export const OptionCard: React.FC<{
  label: string;
  description?: string;
  dotColor?: string;
  selected?: boolean;
  disabled?: boolean;
  // Locked / not-yet-available option — full-colour text (not dimmed), neutral
  // outline (no brandy), and its badge renders in brandy. Not clickable.
  comingSoon?: boolean;
  badge?: string;
  onClick?: () => void;
}> = ({
  label,
  description,
  dotColor,
  selected = false,
  disabled = false,
  comingSoon = false,
  badge,
  onClick
}) => {
  const inactive = disabled || comingSoon;
  return (
  <Box
    data-testid="option-card"
    onClick={inactive ? undefined : onClick}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      px: '14px',
      py: '11px',
      borderRadius: '6px',
      cursor: inactive ? 'default' : 'pointer',
      // Coming-soon keeps full-colour text; only truly-disabled options dim.
      opacity: disabled ? 0.5 : 1,
      // Unselected cards carry a brandy outline (the "act here" highlight);
      // disabled/coming-soon use a plain neutral outline instead.
      border: selected
        ? '1.5px solid #f8de89'
        : inactive
        ? `1px solid ${t.borderContrast}`
        : '1.5px solid rgba(247,148,32,0.55)',
      background: selected ? t.brandyFill : t.surface,
      transition: 'filter 0.15s ease, border-color 0.15s ease',
      '&:hover': inactive
        ? undefined
        : selected
        ? { filter: 'brightness(1.08)' }
        : { borderColor: t.brandLight }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {dotColor && (
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flex: '0 0 auto' }} />
      )}
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: selected ? 600 : 500,
          lineHeight: 1.3,
          color: selected ? t.textPrimary : t.textSecondary
        }}
      >
        {label}
      </Typography>
      {badge && (
        <Typography
          sx={{
            ml: '2px',
            px: '6px',
            py: '1px',
            borderRadius: '4px',
            border: '1px solid rgba(247,148,32,0.55)',
            fontSize: 10,
            fontWeight: 600,
            lineHeight: '13px',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#F79420',
            whiteSpace: 'nowrap'
          }}
        >
          {badge}
        </Typography>
      )}
    </Box>
    {description && (
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '17px',
          color: selected ? 'rgba(255,255,255,0.85)' : t.textTertiary
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
  );
};
